import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useCreateTranslationMutation } from "./translation-service"
import VirtualKeyboard from "./VirtualKeyboard"


interface TranslationProps {
  open: boolean
  onClose: () => void
}

type Translation = {
  fromText: string;
  toText: string;
  direction: "E2G" | "G2E";
};

export default function Translation({ open, onClose }: TranslationProps) {
  const [text, setText] = useState("")
  const [output, setOutput] = useState("")
  const [targetLang, setTargetLang] = useState("gu") // default Gujarati
  const [loading, setLoading] = useState(false)
  const [translations, setTranslations] = useState<Translation[]>([]); // local store
  const [createTranslation] = useCreateTranslationMutation();
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const handleTranslate = async () => {
    if (!text) return;

    const direction = targetLang === "gu" ? "E2G" : "G2E";

    // 1️⃣ Check if translation already exists locally
    const exists = translations.some(
      (t) => t.fromText.trim() === text.trim() && t.direction === direction
    );

    if (exists) {
      const existing = translations.find(
        (t) => t.fromText.trim() === text.trim() && t.direction === direction
      );
      setOutput(existing?.toText || "");
      return; // skip API call
    }

    setLoading(true);
    try {
      // 2️⃣ Call unofficial Google Translate API
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${targetLang == "gu" ? "en" : "gu"}&tl=${targetLang}&dt=t&q=${encodeURIComponent(
          text
        )}`
      );
      const data = await res.json();
      const translated = data[0]?.map((item: any) => item[0]).join(" ");
      setOutput(translated);
      setLoading(false);

      // 3️⃣ Save to backend (optional) via RTK Query
      await createTranslation({
        fromText: text,
        toText: translated,
        direction,
      }).unwrap();

      // 4️⃣ Save locally for future duplicate checks
      setTranslations((prev) => [
        ...prev,
        { fromText: text.trim(), toText: translated, direction },
      ]);

    } catch (err) {
      setOutput("Error translating text");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-lg overflow-y-auto overflow-x-hidden sm:w-full">
        <DialogHeader>
          <DialogTitle>Translate</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Enter text and translate between English ↔ Gujarati.
          </DialogDescription>
        </DialogHeader>

        {/* Input box */}
        <Input
          placeholder={"Enter text in " + (targetLang == "gu" ? "English" : "Gujarati")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-2 text-sm sm:text-base"
        />

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 mt-2">
          <Button 
            onClick={handleTranslate} 
            disabled={loading}
            className="w-full sm:w-auto flex-1 sm:flex-initial text-xs sm:text-sm"
          >
            {loading ? "Translating..." : ("Translate In " + (targetLang == "gu" ? "ગુજરાતી" : "English"))}
          </Button>

          <Button 
            onClick={() => setText("")} 
            variant="outline"
            className="w-full sm:w-auto text-xs sm:text-sm"
          >
            Clear
          </Button>

          <Select value={targetLang} onValueChange={setTargetLang}>
            <SelectTrigger className="w-full sm:w-[150px] text-xs sm:text-sm">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gu">Gujarati</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </div>


        <div className="flex flex-wrap gap-2 mt-2">
          <Button 
            onClick={() => { setKeyboardOpen(true); setTargetLang("gu"); }} 
            className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm flex-1 sm:flex-initial"
          >
            English
          </Button>
          <Button 
            onClick={() => { setKeyboardOpen(true); setTargetLang("en"); }} 
            className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm flex-1 sm:flex-initial"
          >
            ગુજરાતી
          </Button>
          <Button 
            onClick={() => setKeyboardOpen(false)} 
            className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm flex-1 sm:flex-initial"
          >
            Close
          </Button>
        </div>


        {/* Virtual Keyboard */}
        {keyboardOpen &&
          <div className="mt-2 -mx-6 sm:-mx-6 sm:mx-0 overflow-x-auto overflow-y-visible">
            <VirtualKeyboard
              input={text}
              onInputChange={setText}
              language={targetLang == "gu" ? "en" : "gu"}
            />
          </div>
        }

        {/* Output textarea */}
        <Textarea
          value={output}
          readOnly
          placeholder="Translated text will appear here"
          className="mt-2 w-full text-sm sm:text-base resize-none"
          rows={4}
        />

        {/* <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
          <Button onClick={handleTranslate} disabled={loading}>
            {loading ? "Translating..." : "Translate"}
          </Button>
        </DialogFooter> */}
      </DialogContent>
    </Dialog>
  )
}
